import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Accueil', href: '#accueil' },
    { label: 'Services', href: '#services' },
    { label: 'À Propos', href: '#about' },
    { label: 'Témoignages', href: '#temoignages' },
    { label: 'FAQ', href: '#faq' },
    { label: 'Contact', href: '#contact' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="bg-secondary/50 backdrop-blur-sm border-b border-border py-2">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between text-sm">
            <div className="flex items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-1 text-yellow-500">
                <span>⭐⭐⭐⭐⭐</span>
                <span className="text-white ml-1">4.9/5</span>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-green-500 font-semibold">99% Réussite</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-yellow-500 hidden md:inline">7j Résultats</span>
              <span className="text-green-500 font-semibold">Consultation Gratuite</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled ? 'bg-background/95 backdrop-blur-lg shadow-lg border-b border-border' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#accueil" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <span className="text-2xl">🔮</span>
                </div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gold-gradient">Marabout<span className="seo-hidden"> Marseille</span></h1>
                <p className="text-xs text-muted-foreground">Voyant Médium</p>
              </div>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent/10 rounded-lg transition-all"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Button asChild variant="outline" size="sm">
                <a href="tel:+33745449641" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  07 45 44 96 41
                </a>
              </Button>
              <Button asChild size="sm" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a href="https://wa.me/33745449641" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp
                </a>
              </Button>
              <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                <a href="https://paypal.me/PierreMarie888" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.332 0 4.053.64 5.108 1.88.947 1.108 1.283 2.609.997 4.457l-.002.013v.013c-.322 2.077-1.35 3.756-2.99 4.86-1.571 1.056-3.586 1.593-5.99 1.593H7.403a.77.77 0 0 0-.757.63l-.77 4.98a.641.641 0 0 1-.633.54h-.167v-.72zm11.755-14.79c-.006.046-.014.092-.022.138-.633 4.088-3.584 5.5-7.13 5.5H9.832a.872.872 0 0 0-.86.736l-.89 5.65-.253 1.6a.457.457 0 0 0 .451.53h3.165a.764.764 0 0 0 .754-.627l.031-.162.598-3.79.038-.208a.764.764 0 0 1 .754-.627h.474c3.072 0 5.477-1.248 6.179-4.86.293-1.51.141-2.77-.635-3.655a3.03 3.03 0 0 0-.868-.67l.003.005-.002-.003-.002-.002a.018.018 0 0 0-.005-.003l-.003-.002-.003-.002-.003-.001-.002-.002-.003-.001-.002-.002-.003-.001-.002-.002h-.002l-.002-.002-.002-.001-.002-.001-.002-.001-.002-.002h-.002l-.001-.002h-.002l-.002-.001-.001-.001-.002-.001-.001-.001-.002-.001-.001-.001-.001-.001-.002-.001H18.83l-.001-.001z"/>
                  </svg>
                  PayPal
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-accent/10 transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-border bg-background/98 backdrop-blur-lg">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-accent/10 rounded-lg transition-all"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-border">
                <Button asChild variant="outline" size="sm" className="w-full">
                  <a href="tel:+33745449641" className="flex items-center justify-center gap-2">
                    <Phone className="h-4 w-4" />
                    07 45 44 96 41
                  </a>
                </Button>
                <Button asChild size="sm" className="w-full bg-primary hover:bg-primary/90">
                  <a href="https://wa.me/33745449641" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    WhatsApp
                  </a>
                </Button>
                <Button asChild size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <a href="https://paypal.me/PierreMarie888" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.332 0 4.053.64 5.108 1.88.947 1.108 1.283 2.609.997 4.457l-.002.013v.013c-.322 2.077-1.35 3.756-2.99 4.86-1.571 1.056-3.586 1.593-5.99 1.593H7.403a.77.77 0 0 0-.757.63l-.77 4.98a.641.641 0 0 1-.633.54h-.167v-.72zm11.755-14.79c-.006.046-.014.092-.022.138-.633 4.088-3.584 5.5-7.13 5.5H9.832a.872.872 0 0 0-.86.736l-.89 5.65-.253 1.6a.457.457 0 0 0 .451.53h3.165a.764.764 0 0 0 .754-.627l.031-.162.598-3.79.038-.208a.764.764 0 0 1 .754-.627h.474c3.072 0 5.477-1.248 6.179-4.86.293-1.51.141-2.77-.635-3.655a3.03 3.03 0 0 0-.868-.67l.003.005-.002-.003-.002-.002a.018.018 0 0 0-.005-.003l-.003-.002-.003-.002-.003-.001-.002-.002-.003-.001-.002-.002-.003-.001-.002-.002h-.002l-.002-.002-.002-.001-.002-.001-.002-.001-.002-.002h-.002l-.001-.002h-.002l-.002-.001-.001-.001-.002-.001-.001-.001-.002-.001-.001-.001-.001-.001-.002-.001H18.83l-.001-.001z"/>
                    </svg>
                    PayPal
                  </a>
                </Button>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
